import SlideControlButton from '@shared/Articles/ArticleSlug/parts/SlideControlButton';
import ProxyImage from '@shared/ProxyImage/ProxyImage';
import classNames from '@sindresorhus/class-names';
import Image from 'next/image';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Swiper as ISwiper} from 'swiper';
import {Swiper, SwiperSlide} from 'swiper/react';

import Close from '@shared/Articles/assets/close_white.svg';

import {useKeypress} from '@utils/hooks/useKeypress';
import useSwiperProgressState from '@utils/hooks/useSwiperProgressState';

import {Props} from '../types';
import styles from './ScreenshotsModal.module.scss';

type ModalProps = {
  initialSlide: number;
  onClose: () => void;
} & Props;

export default function ScreenshotsModal({
  initialSlide,
  type,
  screenShotsList,
  onClose,
}: ModalProps) {
  useEffect(() => {
    document.body.classList.add(styles.disableBodyScroll);
    return () => {
      document.body.classList.remove(styles.disableBodyScroll);
    };
  }, []);

  const swiperRef = useRef<ISwiper | null>(null);
  const [isClosing, setIsClosing] = useState(false);
  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 300);
  }, [onClose]);

  useKeypress(['Escape'], handleClose);
  useKeypress(['Left', 'ArrowLeft'], () => swiperRef.current?.slidePrev());
  useKeypress(['Right', 'ArrowRight'], () => swiperRef.current?.slideNext());

  const {swiperState, onProgress} = useSwiperProgressState();
  const onContainerClick = useCallback(
    (e: React.MouseEvent) => e.stopPropagation(),
    [],
  );

  return (
    <div
      className={classNames(styles.root, isClosing && styles.closingAnimation)}
      onClick={handleClose}
    >
      <div
        className={classNames(styles.swiperContainer, styles[type])}
        onClick={onContainerClick}
      >
        <Swiper
          onSwiper={(swiper) => (swiperRef.current = swiper)}
          slidesPerView={1}
          initialSlide={initialSlide}
          className={classNames(styles.swiper, styles[type])}
          onProgress={onProgress}
        >
          {!!screenShotsList?.length &&
            screenShotsList.map((elem) => (
              <SwiperSlide key={elem.url} className={styles.slide}>
                <ProxyImage
                  imageData={elem}
                  alt="screenshot"
                  width={elem.width}
                  height={elem.height}
                  className={classNames(styles.image, styles[type])}
                />
              </SwiperSlide>
            ))}
        </Swiper>
        {!swiperState?.isEnd && (
          <SlideControlButton
            standardPosition
            onClick={() => swiperRef.current?.slideNext()}
            direction="next"
            className={classNames(styles.buttonNext, styles[type])}
          />
        )}
        {!swiperState?.isBeginning && (
          <SlideControlButton
            standardPosition
            onClick={() => swiperRef.current?.slidePrev()}
            direction="prev"
            className={classNames(styles.buttonPrev, styles[type])}
          />
        )}
      </div>
      <button className={styles.closeButton} onClick={handleClose}>
        <Image src={Close} width={24} height={24} alt="закрыть" />
      </button>
    </div>
  );
}
